import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { signupSchema, type SignupInput } from '@/shared/lib/validations'
import { useAuthStore } from '@/shared/stores/auth'
import { useAuthDialogStore } from '@/features/auth/stores/auth-dialog'
import { apiClient } from '@/shared/lib/api'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { clearStoredReferralCode, getStoredReferralCode } from '../lib/referral'
import type { AuthResponse } from '../types'

export function SignUpDialog() {
  const { setToken, setUser } = useAuthStore()
  const { isOpen, close, switchView, onSuccess } = useAuthDialogStore()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
  })

  const onSubmit = async (data: SignupInput) => {
    setIsLoading(true)
    setError(null)
    try {
      const referralCode = getStoredReferralCode()
      const response = await apiClient<AuthResponse>('/auth/register', {
        method: 'POST',
        skipAuthRefresh: true,
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          ...(referralCode ? { referralCode } : {}),
        }),
      })
      if (response.refreshToken) setToken(response.refreshToken)
      setUser(response.user)
      clearStoredReferralCode()
      close()
      onSuccess?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign up')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent className="!bottom-0 !left-0 !right-0 !top-auto !translate-x-0 !translate-y-0 rounded-none border-x-0 border-b-0 sm:!left-[50%] sm:!top-[50%] sm:!translate-x-[-50%] sm:!translate-y-[-50%] sm:!bottom-auto sm:!right-auto sm:max-w-md sm:rounded-lg sm:border">
        <DialogHeader className="text-center">
          <DialogTitle>Sign Up</DialogTitle>
          <DialogDescription>Create a new account to get started</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="space-y-2">
            <Label htmlFor="signup-email">Email</Label>
            <Input
              id="signup-email"
              type="email"
              placeholder="you@example.com"
              disabled={isLoading}
              {...register('email')}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="signup-password">Password</Label>
            <Input
              id="signup-password"
              type="password"
              placeholder="At least 8 characters"
              disabled={isLoading}
              {...register('password')}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="signup-confirm">Confirm Password</Label>
            <Input
              id="signup-confirm"
              type="password"
              placeholder="Confirm your password"
              disabled={isLoading}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>

          <Button type="submit" variant="gradient" className="w-full" disabled={isLoading}>
            {isLoading ? 'Creating account...' : 'Sign Up'}
          </Button>
        </form>

        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            Or sign up with
            <span className="h-px flex-1 bg-border" />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              variant="outline"
              onClick={() => switchView('lnurl-signup')}
              disabled={isLoading}
            >
              Lightning
            </Button>
            <Button
              variant="outline"
              onClick={() => switchView('auth47-signup')}
              disabled={isLoading}
            >
              Auth47
            </Button>
          </div>
        </div>

        <p className="mt-4 text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => switchView('signin')}
            className="cursor-pointer border-none bg-transparent p-0 text-primary hover:underline"
          >
            Sign in
          </button>
        </p>
      </DialogContent>
    </Dialog>
  )
}
